import { useEffect, useState } from 'react';
import { bankSavingApi, type CloseContractPayload, type OpenContractPayload, type RotateContractPayload, type WithdrawContractPayload } from '../lib/api';
import type { Asset, BankSavingContract } from '@asset-mgmt/shared';
import { useToast } from '../context/ToastContext';

// ── Helpers ──────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {children}
    </label>
  );
}

const inp =
  'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 outline-none transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 dark:[color-scheme:dark]';

function fmtNum(v: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${v.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`;
  }
}

/** Add `n` months to a YYYY-MM-DD string. */
function addMonths(dateStr: string, months: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** Compute estimated maturity value: principal × (1 + rate/100/12 × months) */
function calcEndValue(principal: number, annualRate: number, months: number): number {
  return principal * (1 + annualRate / 100 / 12 * months);
}

type ActionMode = 'open' | 'close' | 'withdraw' | 'rotate';
type Step = 'select-action' | 'select-contract' | 'form';

// ── Component ──────────────────────────────────────────────────────────────────

export function BankSavingContractModal({
  asset,
  bankAssets,
  initialMode,
  initialContract,
  onClose,
  onSuccess,
}: {
  asset: Asset;
  bankAssets: Asset[];
  /** Pre-select action. If omitted, user picks from a selector first. */
  initialMode?: ActionMode;
  /** Pre-select contract (required when initialMode is 'close', 'withdraw', or 'rotate'). */
  initialContract?: BankSavingContract;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const now   = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // ── Navigation state ─────────────────────────────────────────────────────
  const [step,            setStep]            = useState<Step>(() => {
    if (initialMode && (initialMode === 'open' || initialContract)) return 'form';
    if (initialMode)          return 'select-contract'; // close/withdraw/rotate without contract
    return 'select-action';
  });
  const [activeMode,     setActiveMode]     = useState<ActionMode | null>(initialMode ?? null);
  const [activeContract, setActiveContract] = useState<BankSavingContract | null>(initialContract ?? null);

  // Contracts for select-contract step (also used for rotate)
  const [contracts,        setContracts]        = useState<BankSavingContract[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  useEffect(() => {
    if (step !== 'select-contract') return;
    setContractsLoading(true);
    bankSavingApi.listContracts(asset.id)
      .then((list) => {
        // rotate only shows overdue contracts; close/withdraw shows all non-closed
        const today = new Date().toISOString().slice(0, 10);
        setContracts(
          activeMode === 'rotate'
            ? list.filter((c) => c.status !== 'closed' && c.estimatedEndDate < today)
            : list.filter((c) => c.status !== 'closed')
        );
      })
      .catch(() => setContracts([]))
      .finally(() => setContractsLoading(false));
  }, [step, asset.id, activeMode]);

  // ── Rotate-mode form state ──────────────────────────────────────────────────
  const [rotateRate,      setRotateRate]      = useState('');
  const [rotatePeriod,    setRotatePeriod]    = useState('');
  const [rotateBankName,  setRotateBankName]  = useState('');
  const [rotateOpenDate,  setRotateOpenDate]  = useState('');
  const [rotatePrincipal, setRotatePrincipal] = useState('');
  const [rotateEndDate,   setRotateEndDate]   = useState('');
  const [rotateEndValue,  setRotateEndValue]  = useState('');

  // When a contract is selected for rotation, seed the rotate fields
  useEffect(() => {
    if (activeMode !== 'rotate' || !activeContract) return;
    setRotateRate(String(activeContract.interestRate));
    setRotatePeriod(String(activeContract.periodMonths));
    setRotateBankName(activeContract.bankName);
    setRotateOpenDate(activeContract.estimatedEndDate);
    setRotatePrincipal(String(activeContract.estimatedEndValue));
  }, [activeMode, activeContract]);

  // Auto-compute new end date and value for rotation
  useEffect(() => {
    if (activeMode !== 'rotate') return;
    const p = parseFloat(rotatePrincipal);
    const r = parseFloat(rotateRate);
    const m = parseInt(rotatePeriod, 10);
    if (rotateOpenDate && m > 0) setRotateEndDate(addMonths(rotateOpenDate, m));
    if (p > 0 && r > 0 && m > 0) setRotateEndValue(String(Math.round(calcEndValue(p, r, m))));
  }, [rotateRate, rotatePeriod, rotatePrincipal, rotateOpenDate, activeMode]);

  // ── Open-mode form state ───────────────────────────────────────────────
  const [bankName,     setBankName]     = useState('');
  const [principal,    setPrincipal]    = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [periodMonths, setPeriodMonths] = useState('');
  const [openDate,     setOpenDate]     = useState(today);
  const [endDate,      setEndDate]      = useState('');
  const [endValue,     setEndValue]     = useState('');
  const [sourceId,     setSourceId]     = useState('');

  useEffect(() => {
    const p = parseFloat(principal);
    const r = parseFloat(interestRate);
    const m = parseInt(periodMonths, 10);
    if (openDate && m > 0) setEndDate(addMonths(openDate, m));
    if (p > 0 && r > 0 && m > 0) setEndValue(String(Math.round(calcEndValue(p, r, m))));
  }, [principal, interestRate, periodMonths, openDate]);

  // ── Close/Withdraw-mode form state ──────────────────────────────────────
  const [withdrawType,        setWithdrawType]        = useState<'full' | 'partial'>('full');
  const [withdrawalPrincipal, setWithdrawalPrincipal] = useState('');
  const [receivedAmount,      setReceivedAmount]       = useState('');
  const [receivedDate,        setReceivedDate]         = useState(today);
  const [targetId,            setTargetId]             = useState('');

  useEffect(() => {
    if (!activeContract) return;
    if (withdrawType === 'full') {
      // close = matured (principal + interest); withdraw = early (principal only)
      setReceivedAmount(
        activeMode === 'close'
          ? String(activeContract.estimatedEndValue)
          : String(activeContract.remainingPrincipal)
      );
    } else {
      setReceivedAmount('');
      setWithdrawalPrincipal('');
    }
  }, [withdrawType, activeContract, activeMode]);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  // ── Handlers ──────────────────────────────────────────────────────────────────

  function selectAction(mode: ActionMode) {
    setActiveMode(mode);
    if (mode === 'open') {
      setStep('form');
    } else {
      setStep('select-contract');
    }
  }

  function selectContract(contract: BankSavingContract) {
    setActiveContract(contract);
    setWithdrawType('full');
    // close = matured (principal + interest); withdraw = early (principal only)
    setReceivedAmount(
      activeMode === 'close'
        ? String(contract.estimatedEndValue)
        : String(contract.remainingPrincipal)
    );
    setStep('form');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (activeMode === 'open') {
        const p  = parseFloat(principal);
        const r  = parseFloat(interestRate);
        const m  = parseInt(periodMonths, 10);
        const ev = parseFloat(endValue);
        if (!bankName.trim())    { setError('Bank name is required.');                        setSaving(false); return; }
        if (isNaN(p) || p <= 0) { setError('Principal must be a positive number.');           setSaving(false); return; }
        if (isNaN(r) || r <= 0) { setError('Interest rate must be positive.');                setSaving(false); return; }
        if (isNaN(m) || m <= 0) { setError('Period (months) must be a positive integer.');    setSaving(false); return; }
        if (!openDate)           { setError('Open date is required.');                         setSaving(false); return; }

        const payload: OpenContractPayload = {
          assetId: asset.id, bankName: bankName.trim(),
          principal: p, currency: asset.currency,
          interestRate: r, periodMonths: m,
          openDate, estimatedEndDate: endDate, estimatedEndValue: ev,
          ...(sourceId ? { sourceAssetId: sourceId } : {}),
        };
        await bankSavingApi.openContract(payload);
        toast.success('Contract opened successfully.');

      } else if (activeMode === 'close') {
        const ra = parseFloat(receivedAmount);
        if (isNaN(ra) || ra <= 0) { setError('Received amount must be positive.'); setSaving(false); return; }
        const payload: CloseContractPayload = {
          receivedAmount: ra, receivedDate,
          ...(targetId ? { targetAssetId: targetId } : {}),
        };
        await bankSavingApi.closeContract(activeContract!.id, payload);
        toast.success('Contract closed successfully.');

      } else if (activeMode === 'withdraw') {
        if (withdrawType === 'full') {
          const ra = parseFloat(receivedAmount);
          if (isNaN(ra) || ra <= 0) { setError('Received amount must be positive.'); setSaving(false); return; }
          const payload: CloseContractPayload = {
            receivedAmount: ra, receivedDate,
            ...(targetId ? { targetAssetId: targetId } : {}),
          };
          await bankSavingApi.closeContract(activeContract!.id, payload);
          toast.success('Contract closed (full withdrawal).');
        } else {
          const wp = parseFloat(withdrawalPrincipal);
          const ra = parseFloat(receivedAmount);
          if (isNaN(wp) || wp <= 0) { setError('Withdrawal principal must be positive.'); setSaving(false); return; }
          if (wp >= activeContract!.remainingPrincipal) {
            setError(`Withdrawal principal must be less than ${fmtNum(activeContract!.remainingPrincipal, activeContract!.currency)}. Use full withdrawal instead.`);
            setSaving(false); return;
          }
          if (isNaN(ra) || ra <= 0) { setError('Received amount must be positive.'); setSaving(false); return; }
          const payload: WithdrawContractPayload = {
            withdrawalPrincipal: wp, receivedAmount: ra, receivedDate,
            ...(targetId ? { targetAssetId: targetId } : {}),
          };
          await bankSavingApi.withdrawPartial(activeContract!.id, payload);
          toast.success('Partial withdrawal recorded.');
        }

      } else if (activeMode === 'rotate') {
        const r = parseFloat(rotateRate);
        const m = parseInt(rotatePeriod, 10);
        const p = parseFloat(rotatePrincipal);
        if (!rotateBankName.trim())  { setError('Bank name is required.'); setSaving(false); return; }
        if (isNaN(r) || r <= 0)      { setError('Interest rate must be positive.'); setSaving(false); return; }
        if (isNaN(m) || m <= 0)      { setError('Period must be a positive integer.'); setSaving(false); return; }
        if (isNaN(p) || p <= 0)      { setError('New principal must be positive.'); setSaving(false); return; }
        if (!rotateOpenDate)         { setError('New open date is required.'); setSaving(false); return; }
        const payload: RotateContractPayload = {
          periodMonths: m,
          interestRate: r,
          bankName:     rotateBankName.trim(),
          principal:    p,
          openDate:     rotateOpenDate,
        };
        await bankSavingApi.rotateContract(activeContract!.id, payload);
        toast.success('Contract rotated — old contract closed, new contract started.');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setSaving(false);
    }
  }

  // ── Title ───────────────────────────────────────────────────────────────────
  const titleMap: Record<Step, string> = {
    'select-action':   'Bank Saving — New Transaction',
    'select-contract': activeMode === 'close' ? 'Select Contract to Close'
                     : activeMode === 'rotate' ? 'Select Overdue Contract to Rotate'
                     : 'Select Contract to Withdraw From',
    'form':            activeMode === 'open'   ? 'Open New Contract'
                     : activeMode === 'close'  ? 'Close Contract'
                     : activeMode === 'rotate' ? 'Rotate Contract'
                     : 'Withdraw from Contract',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:p-4 sm:items-center">
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 px-5 py-4">
          <div className="flex items-center gap-2">
            {step !== 'select-action' && !initialMode && (
              <button
                type="button"
                onClick={() => {
                  if (step === 'form' && activeMode !== 'open') setStep('select-contract');
                  else { setStep('select-action'); setActiveMode(null); }
                }}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{titleMap[step]}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Step 1: Action selector ── */}
        {step === 'select-action' && (
          <div className="p-5 space-y-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              What would you like to do with <strong className="text-gray-700 dark:text-gray-200">{asset.name}</strong>?
            </p>
            {([
              { mode: 'open'     as ActionMode, label: 'Open New Contract',      desc: 'Start a new term deposit',                 icon: '🏦', color: 'border-teal-300 dark:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/30' },
              { mode: 'withdraw' as ActionMode, label: 'Withdraw from Contract',  desc: 'Partial or full early withdrawal',          icon: '💸', color: 'border-yellow-300 dark:border-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/30' },
              { mode: 'close'    as ActionMode, label: 'Close Contract',          desc: 'Matured contract — collect full amount',    icon: '✅', color: 'border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/30' },
              { mode: 'rotate'   as ActionMode, label: 'Rotate Contract',         desc: 'Roll over overdue contract into a new term', icon: '🔄', color: 'border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30' },
            ] as const).map(({ mode, label, desc, icon, color }) => (
              <button
                key={mode}
                type="button"
                onClick={() => selectAction(mode)}
                className={`w-full flex items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-colors ${color}`}
              >
                <span className="text-2xl">{icon}</span>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{desc}</div>
                </div>
                <svg className="ml-auto h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        )}

        {/* ── Step 2: Contract picker ── */}
        {step === 'select-contract' && (
          <div className="p-5 space-y-3">
            {contractsLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
              </div>
            ) : contracts.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                {activeMode === 'rotate' ? 'No overdue contracts found.' : 'No active contracts found.'}
              </p>
            ) : (
              contracts.map((contract) => {
                const isOverdue = contract.estimatedEndDate < new Date().toISOString().slice(0, 10);
                return (
                <button
                  key={contract.id}
                  type="button"
                  onClick={() => selectContract(contract)}
                  className={`w-full rounded-xl border-2 p-3.5 text-left transition-colors ${
                    isOverdue
                      ? 'border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-teal-400 dark:hover:border-teal-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      {contract.bankName}
                      {isOverdue && <span className="ml-1.5 text-orange-500">⚠</span>}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      isOverdue
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        : contract.status === 'active'
                          ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                    }`}>{isOverdue ? 'overdue' : contract.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {fmtNum(contract.remainingPrincipal, contract.currency)}
                    {' · '}{contract.interestRate}% p.a.
                    {' · '}{contract.periodMonths}m
                    {' · '}ends {contract.estimatedEndDate}
                  </div>
                  <div className="mt-0.5 text-xs text-teal-600 dark:text-teal-400">
                    Est. maturity: {fmtNum(contract.estimatedEndValue, contract.currency)}
                  </div>
                </button>
                );
              })
            )}
          </div>
        )}

        {/* ── Step 3: Form ── */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4 p-5">
            {/* Open form */}
            {activeMode === 'open' && (
              <>
                <Field label="Bank Name">
                  <input className={inp} type="text" placeholder="e.g. Vietcombank"
                    value={bankName} onChange={(e) => setBankName(e.target.value)} required />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label={`Principal (${asset.currency})`}>
                    <input className={inp} type="number" min="0" step="any" placeholder="0"
                      value={principal} onChange={(e) => setPrincipal(e.target.value)} required />
                  </Field>
                  <Field label="Annual Interest Rate (%)">
                    <input className={inp} type="number" min="0" step="any" placeholder="e.g. 5.5"
                      value={interestRate} onChange={(e) => setInterestRate(e.target.value)} required />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Period (months)">
                    <input className={inp} type="number" min="1" step="1" placeholder="e.g. 12"
                      value={periodMonths} onChange={(e) => setPeriodMonths(e.target.value)} required />
                  </Field>
                  <Field label="Open Date">
                    <input className={inp} type="date" value={openDate}
                      onChange={(e) => setOpenDate(e.target.value)} required />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Estimated End Date">
                    <div className={`${inp} bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 cursor-default`}>
                      {endDate || <span className="text-gray-400">—</span>}
                    </div>
                  </Field>
                  <Field label={`Est. End Value (${asset.currency})`}>
                    <div className={`${inp} bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 cursor-default`}>
                      {endValue ? fmtNum(parseFloat(endValue), asset.currency) : <span className="text-gray-400">—</span>}
                    </div>
                  </Field>
                </div>

                {endValue && principal && (
                  <p className="text-xs text-teal-600 dark:text-teal-400">
                    Estimated interest:{' '}
                    <strong>{fmtNum(parseFloat(endValue) - parseFloat(principal), asset.currency)}</strong>
                  </p>
                )}

                <Field label="Fund from bank account (optional)">
                  <select className={inp} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                    <option value="">— Skip —</option>
                    {bankAssets.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            {/* Close / Withdraw form */}
            {(activeMode === 'close' || activeMode === 'withdraw') && activeContract && (
              <>
                {/* Contract summary */}
                <div className="rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800 p-3 space-y-1 text-sm">
                  <div className="font-semibold text-teal-800 dark:text-teal-300">{activeContract.bankName}</div>
                  <div className="text-gray-600 dark:text-gray-400">
                    Principal: <strong>{fmtNum(activeContract.principal, activeContract.currency)}</strong>
                    {activeContract.remainingPrincipal !== activeContract.principal && (
                      <> · Remaining: <strong>{fmtNum(activeContract.remainingPrincipal, activeContract.currency)}</strong></>
                    )}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    {activeContract.interestRate}% p.a. · {activeContract.periodMonths} months
                    · {activeContract.openDate} → {activeContract.estimatedEndDate}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">
                    Est. maturity: <strong>{fmtNum(activeContract.estimatedEndValue, activeContract.currency)}</strong>
                  </div>
                </div>

                {/* Withdrawal type toggle */}
                {activeMode === 'withdraw' && (
                  <div className="flex gap-2">
                    {(['full', 'partial'] as const).map((t) => (
                      <button key={t} type="button" onClick={() => setWithdrawType(t)}
                        className={`flex-1 rounded-xl border py-2 text-sm font-medium transition-colors ${
                          withdrawType === t
                            ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-600'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                        }`}>
                        {t === 'full' ? 'Full Withdrawal' : 'Partial Withdrawal'}
                      </button>
                    ))}
                  </div>
                )}

                {activeMode === 'withdraw' && withdrawType === 'partial' && (
                  <Field label={`Principal to withdraw (max: ${fmtNum(activeContract.remainingPrincipal, activeContract.currency)})`}>
                    <input className={inp} type="number" min="0" max={activeContract.remainingPrincipal - 1} step="any"
                      placeholder="0" value={withdrawalPrincipal}
                      onChange={(e) => setWithdrawalPrincipal(e.target.value)} required />
                  </Field>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label={`Received amount (${activeContract.currency})`}>
                    <input className={inp} type="number" min="0" step="any" placeholder="0"
                      value={receivedAmount} onChange={(e) => setReceivedAmount(e.target.value)} required />
                  </Field>
                  <Field label="Received Date">
                    <input className={inp} type="date" value={receivedDate}
                      onChange={(e) => setReceivedDate(e.target.value)} required />
                  </Field>
                </div>

                {receivedAmount && (() => {
                  const ra   = parseFloat(receivedAmount);
                  const base = activeMode === 'withdraw' && withdrawType === 'partial'
                    ? parseFloat(withdrawalPrincipal) || 0
                    : activeContract.remainingPrincipal;
                  const interest = ra - base;
                  return interest !== 0 ? (
                    <p className={`text-xs ${interest > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-red-500'}`}>
                      {interest > 0 ? 'Interest earned: ' : 'Early withdrawal loss: '}
                      <strong>{fmtNum(Math.abs(interest), activeContract.currency)}</strong>
                    </p>
                  ) : null;
                })()}

                <Field label="Credit to bank account (optional)">
                  <select className={inp} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                    <option value="">— Skip —</option>
                    {bankAssets.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            {/* Rotate form */}
            {activeMode === 'rotate' && activeContract && (
              <>
                {/* What's happening summary */}
                <div className="rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 p-3 space-y-1 text-sm">
                  <p className="font-semibold text-orange-800 dark:text-orange-300">🔄 Rotating overdue contract</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Old contract <strong>{activeContract.bankName}</strong> (matured {activeContract.estimatedEndDate}) will be closed.
                    Its maturity value <strong className="text-orange-700 dark:text-orange-300">{fmtNum(activeContract.estimatedEndValue, activeContract.currency)}</strong> becomes the new principal.
                  </p>
                </div>

                <Field label="Bank Name">
                  <input className={inp} type="text" value={rotateBankName}
                    onChange={(e) => setRotateBankName(e.target.value)} required />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Annual Interest Rate (%)">
                    <input className={inp} type="number" min="0" step="any" placeholder="e.g. 5.5"
                      value={rotateRate} onChange={(e) => setRotateRate(e.target.value)} required />
                  </Field>
                  <Field label="Period (months)">
                    <input className={inp} type="number" min="1" step="1" placeholder="e.g. 12"
                      value={rotatePeriod} onChange={(e) => setRotatePeriod(e.target.value)} required />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="New Open Date">
                    <input className={inp} type="date" value={rotateOpenDate}
                      onChange={(e) => setRotateOpenDate(e.target.value)} required />
                  </Field>
                  <Field label={`New Principal (${activeContract.currency})`}>
                    <input className={inp} type="number" min="0" step="any"
                      value={rotatePrincipal} onChange={(e) => setRotatePrincipal(e.target.value)} required />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="New End Date">
                    <div className={`${inp} bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 cursor-default`}>
                      {rotateEndDate || <span className="text-gray-400">—</span>}
                    </div>
                  </Field>
                  <Field label={`Est. End Value (${activeContract.currency})`}>
                    <div className={`${inp} bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 cursor-default`}>
                      {rotateEndValue ? fmtNum(parseFloat(rotateEndValue), activeContract.currency) : <span className="text-gray-400">—</span>}
                    </div>
                  </Field>
                </div>

                {rotateEndValue && (
                  <p className="text-xs text-teal-600 dark:text-teal-400">
                    Estimated interest on new contract:{' '}
                    <strong>{fmtNum(parseFloat(rotateEndValue) - parseFloat(rotatePrincipal || '0'), activeContract.currency)}</strong>
                  </p>
                )}
              </>
            )}

            {error && (
              <p className="rounded-xl bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className={`flex-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50 ${
                  activeMode === 'rotate'
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}>
                {saving ? 'Saving…' : activeMode === 'open' ? 'Open Contract' : activeMode === 'rotate' ? 'Rotate Contract' : 'Confirm'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
